/**
 * Хук для загрузки дневной статистики одного Instagram аккаунта.
 * Используется на странице детальной аналитики для построения графика.
 */

import { useEffect, useRef, useState } from 'react';

import { fetchAccountDailyInsights } from '../api/account-daily-insights';
import { toInstagramSince, toInstagramUntil } from '../lib/date-range';

import type { AccountDailyInsightsResponse, DayDataPoint } from '../api/account-daily-insights';
import type { ResolvedDateRange } from '../types/account-stats';

export type { DayDataPoint };

export type PeriodTotals = AccountDailyInsightsResponse['totals'];

type State = {
  days: DayDataPoint[];
  totals: PeriodTotals | null;
  prevTotals: PeriodTotals | null;
  username: string | null;
  name: string | null;
  avatarUrl: string | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
};

const INITIAL_STATE: State = {
  days: [],
  totals: null,
  prevTotals: null,
  username: null,
  name: null,
  avatarUrl: null,
  isLoading: true,
  isFetching: false,
  error: null,
};

export function useAccountDailyInsights(
  accountId: string,
  dateRange: ResolvedDateRange,
  comparePrev: boolean
): State {
  const [state, setState] = useState<State>(INITIAL_STATE);
  const abortRef = useRef<AbortController | null>(null);

  const since = toInstagramSince(dateRange.from);
  const until = toInstagramUntil(dateRange.to);
  const prevSince = dateRange.prevFrom ? toInstagramSince(dateRange.prevFrom) : null;
  const prevUntil = dateRange.prevTo ? toInstagramUntil(dateRange.prevTo) : null;

  useEffect(() => {
    if (!accountId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const fetchMain = fetchAccountDailyInsights({ accountId, since, until });
    const fetchPrev =
      comparePrev && prevSince !== null && prevUntil !== null
        ? fetchAccountDailyInsights({ accountId, since: prevSince, until: prevUntil })
        : Promise.resolve(null);

    Promise.all([fetchMain, fetchPrev])
      .then(([mainData, prevData]) => {
        if (controller.signal.aborted) return;
        setState({
          days: mainData.days,
          totals: mainData.totals,
          prevTotals: prevData?.totals ?? null,
          username: mainData.username,
          name: mainData.name,
          avatarUrl: mainData.avatar_url,
          isLoading: false,
          isFetching: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(prev => ({
          ...prev,
          isLoading: false,
          isFetching: false,
          error: err instanceof Error ? err : new Error(String(err)),
        }));
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, since, until, comparePrev, prevSince, prevUntil]);

  return state;
}
